/**
 * MOCK enrollment adapter. Stands in for backend endpoints that do not exist yet
 * (see httpEnrollmentApi.js for the proposed contract). Nothing here reaches a
 * server; every UI that uses it shows a visible "mock" notice.
 *
 * It mirrors the one server behavior the UI depends on (blueprint 3.1): a repeated
 * submission_idempotency_key returns the EXISTING enrollment instead of creating a
 * second one.
 *
 * The default connector is `manual_referral`, which declares zero extension fields.
 * That is the truth today: no supplier is signed, so no connector adds fields yet.
 */
import { EnrollmentApiError } from "./httpEnrollmentApi";

const enrollmentsByKey = new Map();
const startsByKey = new Map();
const waitlist = [];
let nextOutcome = null; // test/dev hook: "indeterminate" | "validation" | "unavailable"

export const mockControls = {
  failNextWith(kind) {
    nextOutcome = kind;
  },
  reset() {
    enrollmentsByKey.clear();
    startsByKey.clear();
    waitlist.length = 0;
    nextOutcome = null;
  },
  get submissions() {
    return Array.from(enrollmentsByKey.values());
  },
  get starts() {
    return Array.from(startsByKey.values());
  },
  get waitlistEntries() {
    return waitlist.slice();
  },
};

const tick = (ms = 50) => new Promise((r) => setTimeout(r, ms));

function consumeForcedFailure(message) {
  if (!nextOutcome) return;
  const kind = nextOutcome;
  nextOutcome = null;
  throw new EnrollmentApiError(kind, message, kind === "validation" ? { phone: "Enter a valid phone number." } : {});
}

export const mockEnrollmentApi = {
  isMock: true,

  async getEnrollmentConfig(planId) {
    await tick();
    return {
      connector_slug: "manual_referral",
      energy_type: "electricity",
      // Marked as a mock so it can never be mistaken for a real, immutable snapshot id.
      plan_rate_snapshot_id: `mock-snapshot-${planId}`,
      extension_fields: [],
    };
  },

  /**
   * Mirrors POST /api/enrollments/starts: records only plan, state, snapshot and the key (no personal data).
   * The same key again is a replay of the existing start, never a second one.
   */
  async startEnrollment({ planId, stateCode, planRateSnapshotId, startKey }) {
    await tick();
    const key = startKey || `mock-start-${startsByKey.size + 1}`;
    const replay = startsByKey.has(key);
    if (!replay) {
      startsByKey.set(key, {
        start_key: key,
        plan_id: planId,
        state: stateCode,
        plan_rate_snapshot_id: planRateSnapshotId,
        started_at: new Date().toISOString(),
      });
    }
    return { start_key: key, started_at: startsByKey.get(key).started_at, idempotent_replay: replay };
  },

  async submitEnrollment(payload) {
    await tick();
    consumeForcedFailure("mock forced failure");
    const key = payload.submission_idempotency_key;
    if (!key) throw new EnrollmentApiError("rejected", "missing idempotency key");
    if (!enrollmentsByKey.has(key)) {
      enrollmentsByKey.set(key, {
        enrollment_id: `mock-enr-${enrollmentsByKey.size + 1}`,
        status: "received", // the API returns a progress key, never the internal state (QA-26)
        next_step: { type: "manual_followup" },
        payload,
      });
    }
    const existing = enrollmentsByKey.get(key);
    return { enrollment_id: existing.enrollment_id, status: existing.status, next_step: existing.next_step };
  },

  async joinWaitlist({ stateCode, track, email }) {
    await tick();
    consumeForcedFailure("mock forced failure");
    waitlist.push({ stateCode, track, email });
  },
};
